// Dependencies
import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRealtimeUnits, useRealtimeDrivers } from 'Core/hooks';

// Components
import { EntitySelector } from 'Components';

// Redux
import {
  catalogLoad,
  getLandmarkGroups,
  searchLandmarks,
  searchLandmarksOverOffset,
} from 'Redux/actions';
import { useIntl } from 'react-intl';

const LANDMARKS_PER_PAGE = 20;

/**
 * Build a component to allow the user select items from entities using the redux store.
 * @param {string} title - Component title.
 * @param {Array} entities - List of entities to show.
 * @param {string} selectedEntitiesTitle - Selected items section title.
 * @param {Component} checkedIcon - Left side icon showed when the item is selected.
 * @param {Component} unCheckedIcon - Right side icon showed when the item is not selected.
 * @param {boolean} onlyItem - It allows to select only one item from a entity.
 * @param {boolean} multiple - It allows to select items from one or more entities (Not Implemented Yet).
 * @param {number} initialSize - Set the number of visible items before click the expand button (default 4).
 * @param {boolean} required - Sets if at least one item should be selected.
 * @param {Function} itemsFilterCallback - It allows to filter the items what will be displayed in the entitySelector.
 * @param {Object} selectedEntity - The selected entity.
 * @param {Function} onChangeItems - Callback to trigger when an item is changed.
 * @param {Function} onChangeEntity - Callback to trigger when an entity is changed.
 * @param {Array} selectedItems - The current selected items.
 * @param {String} requiredMessage - Text to show the warning when the prop is required.
 * @param {String} expandButtonLabel - Expand button label.
 * @param {String} collapseButtonLabel - Collapse button label.
 */
const EntitySelectorComponent = (props) => {
  const {
    title,
    entities,
    selectedEntitiesTitle,
    checkedIcon,
    unCheckedIcon,
    onlyItem,
    required,
    initialSize,
    itemsFilterCallback,
    selectedEntity,
    selectedEntityDefault,
    onChangeItems,
    onChangeEntity,
    selectedItems,
    requiredMessage,
    hideSelectedItems,
    triggerCollapse,
    expandButtonLabel,
    collapseButtonLabel,
    multiple,
    colorSelection,
    onChangeSelectAll,
    selectAllItems,
    isReport
  } = props;
  const {
    landmarks,
    landmarkGroups,
    totalPaginatedItems,
  } = useSelector((state) => state.entitySelectorRedux);
  const {messages} = useIntl()

  const dispatch = useDispatch();
  const [entitiesObj, setEntitiesObj] = useState([]);
  const [landmarksOffset, setLandmarksOffset] = useState(landmarks.length ? landmarks.length - 20 : 0);
  const [searchText, setSearchText] = useState('');
  const [extraItemsToSearch, setExtraItemsToSearch] = useState([]);
  const [shouldClearLandmarks, setShouldClearLandmarks] = useState(false);

  const unitsList = useRealtimeUnits({ trackFields: ['id', 'name', 'label', 'disabled', 'driverId', 'vehicleStatusId', 'tags'] });
  const drivers = useRealtimeDrivers({ trackFields: ['id', 'name', 'externalId', 'disabled', 'tags'] });
  const pmDevices = useSelector((state) => state.catalogsRedux.pmDevices);
  const unitGroups = useSelector((state) => state.catalogsRedux.unitGroupsList);
  const driverGroups = useSelector((state) => state.catalogsRedux.driverGroups);
  const contacts = useSelector((state) => state.catalogsRedux.contacts);

  useEffect(() => {
    let catalogsToLoad = [];
    let entitiesObjTemp = [];

    entities.forEach((entity) => {
      const { id, label } = entity;

      switch (id?.toLowerCase()) {
        case "units":
          entitiesObjTemp.push({ id, name: label, items: [], loading: true });
          catalogsToLoad.push("unitsList");
          break;
        case "unitsVideo":
          entitiesObjTemp.push({ id, name: label, items: [], loading: true });
          catalogsToLoad.push("unitsList");
          catalogsToLoad.push("pmDevices");
          break;
        case "drivers":
          entitiesObjTemp.push({ id, name: label, items: [], loading: true });
          catalogsToLoad.push("drivers");
          break;
        case "unitGroups":
          entitiesObjTemp.push({ id, name: label, items: [], loading: true });
          catalogsToLoad.push("unitGroupsList");
          break;
        case "driverGroups":
          entitiesObjTemp.push({ id, name: label, items: [], loading: true });
          catalogsToLoad.push("driverGroups");
          break;
        case "landmarks":
          entitiesObjTemp.push({ id, name: label, items: [], loading: true });
          break;
        case "landmarkGroups":
          entitiesObjTemp.push({ id, name: label, items: [], loading: true });
          break;
        case "all":
          entitiesObjTemp.push({ id, name: label, items: [], loading: false });
          break;
        case "contacts":
          entitiesObjTemp.push({ id, name: label, items: [], loading: false });
          break;
      }
    });

    // Getting catalogs
    dispatch(catalogLoad(catalogsToLoad));
    dispatch(getLandmarkGroups());

    // Save the entities to show
    setEntitiesObj(entitiesObjTemp);
  }, []);

  useEffect(()=>{
    if(entitiesObj.length === 0) return
    const temp = entitiesObj.map(ent => ({...ent, name: entities.find(t=>t.id===ent.id).label}))
    setEntitiesObj(temp)
  }, [entities])

  const notEmptyItems = (
    unitsList.length > 0
    || drivers.length > 0
    || unitGroups.length > 0
    || driverGroups.length > 0
    || landmarks.length > 0
    || landmarkGroups.length > 0
    || contacts.length > 0
    || pmDevices.length > 0
  );

  useEffect(() => {
    const updateEntity = () => {
      const typeNames = {
        Unit: { id: "units", label: "Units" },
        UnitVideo: { id: "unitsVideo", label: "Units" },
        UnitGroup: { id: "unitGroups", label: "Units Groups" },
        Driver: { id: "drivers", label: "Drivers" },
        DriverGroup: { id: "driverGroups", label: "Drivers Groups" },
        Landmark: { id: "landmarks", label: "Landmarks" },
        LandmarkGroup: { id: "landmarkGroups", label: "Landmark Groups" },
        All: { id: "all", label: "All" },
      };

      const getPopulatedEntity = (givenEntity) => entitiesObj.find((entity) => entity?.id == givenEntity?.id);
      const givenEntity = typeNames[selectedItems[0]?.__typename];
      const currentEntity = getPopulatedEntity(givenEntity) || getPopulatedEntity(selectedEntity);

      return currentEntity && onChangeEntity(currentEntity);
    };

    updateEntity();
  }, [JSON.stringify(entitiesObj), JSON.stringify(selectedEntity)]);

  useEffect(() => {
    function getLandmarks(landmarksPerPage, offset, searchText, shouldClearLandmarks) {
      return dispatch(searchLandmarks(landmarksPerPage, offset, searchText, shouldClearLandmarks));
    };

    // Performs a search if the offset is increased by the user.
    if (landmarks.length <= landmarksOffset) {
      getLandmarks(LANDMARKS_PER_PAGE, landmarksOffset, '', shouldClearLandmarks);
      setShouldClearLandmarks(false);
    };

    // Performs landmarks search if there is a given paran in the search input.
    if (searchText !== '') {
      setLandmarksOffset(0);
      setShouldClearLandmarks(true);
      getLandmarks(0, 0, searchText, shouldClearLandmarks);
    };
  }, [landmarksOffset, searchText]);

  const landmarksEntityCallback = useCallback(
    (newOffset) => {
      return setLandmarksOffset(newOffset);
    },
    [landmarksOffset],
  );

  useEffect(() => {
    (items) => {
      return dispatch(searchLandmarksOverOffset(items));
    }
  }, [extraItemsToSearch]);
  // When the catalog received
  useEffect(() => {
    if (entitiesObj.length > 0 && notEmptyItems) {
      let newDrivers = drivers.map((driver) => ({ id: driver.driverId, ...driver }));
      if(isReport){
        const items = drivers.filter((value, index, self) =>
          index === self.findIndex((t) => (
            t.driverId === value.driverId
          ))
        )
        newDrivers = JSON.parse(JSON.stringify(items));
        newDrivers = newDrivers.map((driver) => ({ id: driver.driverId, ...driver }));
      }
      let entitiesObjTemp = entitiesObj.map((entity) => {
        let entityObj = entity;
        switch (entity.id) {
          case "units":
            entityObj = {
              ...entity,
              items: unitsList,
              loading: false,
              fieldText: "label",
              fieldValue: "id",
            };
            break;
          case "unitsVideo":
            let pmDevicesIds = [];
            pmDevices.forEach(pmDevice => {
              if (pmDevice?.deviceId) pmDevicesIds.push(`${pmDevice?.deviceId}`);
            });

            entityObj = {
              ...entity,
              items: unitsList.filter(unit => pmDevicesIds.includes(unit?.id) || pmDevicesIds.includes(`${unit?.id}`)),
              loading: false,
              fieldText: "label",
              fieldValue: "id",
            };
            break;
          case "drivers":
            entityObj = {
              ...entity,
              items: newDrivers,
              loading: false,
              fieldText: "name",
              fieldValue: "id",
            };
            break;
          case "unitGroups":
            entityObj = {
              ...entity,
              items: unitGroups,
              loading: false,
              fieldText: "name",
              fieldValue: "id",
            };
            break;
          case "driverGroups":
            entityObj = {
              ...entity,
              items: driverGroups,
              loading: false,
              fieldText: "name",
              fieldValue: "id",
            };
            break;
          case "landmarks":
            entityObj = {
              ...entity,
              items: landmarks,
              loading: false,
              fieldText: "name",
              fieldValue: "id",
              entityCallback: landmarksEntityCallback,
              entityOffset: landmarksOffset,
              totalPaginatedItems: totalPaginatedItems,
              entitySearch: (newText) => setSearchText(newText),
              landmarksPerPage: LANDMARKS_PER_PAGE,
              searchOverOffset: (items) => setExtraItemsToSearch(items),
            };
            break;
          case "landmarkGroups":
            entityObj = {
              ...entity,
              items: landmarkGroups,
              loading: false,
              fieldText: "name",
              fieldValue: "id",
            };
            break;
          case "contacts":
            entityObj = {
              ...entity,
              items: contacts.map((contact) => ({ name: contact.title, ...contact })),
              loading: false,
              fieldText: "name",
              fieldValue: "id",
            };
            break;
        }

        return entityObj
      });

      setEntitiesObj(entitiesObjTemp);
    }
  }, [
    unitsList,
    pmDevices,
    drivers,
    unitGroups,
    driverGroups,
    landmarks,
    landmarkGroups,
    totalPaginatedItems,
  ]); //...add others
  return (
    <EntitySelector
      title={title}
      entities={entitiesObj}
      selectedItems={selectedItems}
      required={required}
      requiredMessage={requiredMessage}
      checkedIcon={checkedIcon}
      unCheckedIcon={unCheckedIcon}
      selectedEntitiesTitle={selectedEntitiesTitle}
      expandButtonLabel={expandButtonLabel || messages['show_all']}
      collapseButtonLabel={collapseButtonLabel || messages['show_less']}
      onlyItem={onlyItem}
      initialSize={initialSize}
      selectedEntityDefault={selectedEntityDefault}
      itemsFilterCallback={itemsFilterCallback}
      triggerCollapse={triggerCollapse}
      loadMoreLabel="Load more"
      searchText={searchText}
      selectedEntity={selectedEntity}
      onChangeItems={onChangeItems}
      onChangeEntity={onChangeEntity}
      hideSelectedItems={hideSelectedItems}
      multiple={multiple}
      colorSelection={colorSelection}
      selectAllItems={selectAllItems}
      onChangeSelectAll={onChangeSelectAll}
    />
  )
}

export default EntitySelectorComponent;